'use client'

import { Button } from '@/core/components/form/button/button'
import { IoHeart } from 'react-icons/io5'
import { LuChevronLeft } from 'react-icons/lu'

import Carousel from '@/core/components/optional/carousel'
import SafeImage from '@/features/veluvit/components/ui/media-image'
import { cn } from '@/core/utils/common'

import {
  ChatIcon,
  ShareIcon,
} from '@/features/veluvit/components/ui/action-icons'
import WorkTitleOverlay from '@/features/veluvit/components/ui/work-title-overlay'
import { useCatalogMedia } from '@/features/veluvit/hooks/use-app-data'
import { kfmt, productCopy } from '@/features/veluvit/lib/format'
import { IMAGE_QUALITY } from '@/features/veluvit/lib/image-quality'
import { back } from '@/features/veluvit/lib/navigation'
import { shareLink } from '@/features/veluvit/services/catalog.actions'

const GALLERY_FILL = {
  frame: 'h-full',
  viewport: 'h-full',
  track: 'h-full',
  slide: 'h-full',
}

const ROUND =
  'grid size-10 cursor-pointer place-items-center rounded-full bg-black/30 text-white no-underline backdrop-blur-md transition-[background-color,scale] duration-200 hover:bg-black/55 focus-visible:bg-black/55 active:scale-95'

/**
 * 작품 상세 머리 — 홈 배너와 같은 말투(2026-10-07 갈아엎기).
 * 바탕은 표지(2:3)를 낮은 화질로 크게 흐린 빛, 가운데(PC 는 왼쪽)에 표지 액자(넘기면 장면 그림), 그 옆/아래에 상품 공통 순서로
 *   작품명 → ♥ 좋아요 · 💬 대화 → #태그 → 제작자 · 등급.
 * 옛 상세의 기울인 라벨(장르 · COMING SOON) · "당신의 답장으로 시작되는 이야기" 문구 · 작품/성향/유형 나열 줄은 뺐다.
 * PC 는 상단 막대 밑까지 바탕 빛을 끌어올린다(내비 유리가 그 빛을 비춘다).
 */
export default function DetailHero({ id }: { id: string }) {
  const { catalog, src, thumb } = useCatalogMedia()
  const c = catalog.chars[id]
  const stats = catalog.works.find((work) => work.id === id)?.stats
  const logo = catalog.images[id]?.logo
  // 실제 이미지가 있는 장면만 갤러리에 넣는다. 표지가 없으면 실제 썸네일 한 장만 쓴다.
  const images = catalog.images[id] || {}
  const seenImages = new Set<string>()
  const kinds = [
    'cover',
    ...Array.from({ length: 9 }, (_, index) => `cover${index + 2}`),
    's1',
    's2',
    'hero',
  ].filter((kind) => {
    const image = images[kind]?.trim()
    if (!image || seenImages.has(image)) return false
    seenImages.add(image)
    return true
  })
  if (kinds.length === 0 && images.thumb) kinds.push('thumb')
  const author = productCopy(c.author)
  const adult = c.style?.rating === 'unsafe'

  return (
    <header className="relative isolate overflow-hidden mt-[calc(var(--safe-top)*-1)] px-4 pt-[calc(var(--safe-top)+12px)] pb-1 over10:-mt-14.5 over10:px-8 over10:pt-[calc(58px+16px)] over10:pb-4">
      {/* 바탕 빛 — 표지를 흐려 깔고 아래로 갈수록 화면 바탕에 녹인다 */}
      <span aria-hidden="true" className="pointer-events-none absolute inset-0">
        <SafeImage
          src={src(id, 'cover')}
          fallbackSrc={thumb(id)}
          alt=""
          fill
          sizes="160px"
          quality={30}
          className="scale-125 object-cover opacity-80 blur-3xl saturate-150"
          appearance="plain"
        />
        <span className="absolute inset-0 bg-[linear-gradient(180deg,rgb(0_0_0/0.4)_0%,rgb(0_0_0/0.2)_45%,var(--color-background)_100%)]" />
      </span>

      <div className="relative mx-auto max-w-[760px]">
        {/* 머리 단추 — 뒤로 · 공유. 컬렉션 페이지는 없어지고 아래 컬렉션 섹션으로 합쳤다(2026-10-07) */}
        <div className="flex items-center justify-between">
          <Button
            type="button"
            aria-label="뒤로"
            onClick={back}
            className={ROUND}
            variant="bare"
          >
            <LuChevronLeft aria-hidden="true" className="size-5" />
          </Button>
          <Button
            type="button"
            aria-label="공유"
            onClick={() => shareLink()}
            className={ROUND}
            variant="bare"
          >
            <ShareIcon aria-hidden="true" className="size-4.25" />
          </Button>
        </div>

        <div className="mt-2 flex flex-col items-center text-center over10:mt-5 over10:flex-row over10:items-end over10:gap-9 over10:text-left">
          {/* 표지 액자 — 2:3. 넘기면 장면 그림, 아래 막대 점으로 몇 번째인지 */}
          <div className="relative aspect-[2/3] w-[min(56vw,224px)] flex-none overflow-hidden rounded-[16px] bg-raised shadow-[0_28px_48px_-18px_rgb(0_0_0/0.9),0_0_0_1px_rgb(255_255_255/0.14)] over10:w-60">
            {kinds.length > 0 ? (
              <Carousel
                ariaLabel={`${c.name} 이미지`}
                className="h-full"
                classNames={GALLERY_FILL}
                showArrows={false}
                slides={kinds.map((k, i) => ({
                  id: k,
                  content: (
                    <div
                      className={cn(
                        'relative h-full w-full',
                        k === 'hero' && 'bg-black'
                      )}
                    >
                      <SafeImage
                        src={src(id, k)}
                        fallbackSrc={thumb(id)}
                        alt=""
                        fill
                        sizes="(min-width: 1001px) 240px, 224px"
                        preload={i === 0}
                        loading={i === 0 ? undefined : 'eager'}
                        quality={IMAGE_QUALITY.scene}
                        className={cn(
                          'object-cover',
                          k === 'hero'
                            ? 'object-contain object-center'
                            : 'object-[50%_20%]'
                        )}
                        appearance="plain"
                      />
                      {i === 0 && <WorkTitleOverlay src={logo} />}
                    </div>
                  ),
                }))}
              />
            ) : (
              <div className="grid h-full place-items-center bg-surface px-4 text-center text-c14 text-t3">
                등록된 이미지가 없어요.
              </div>
            )}
            {/* 점 막대가 읽히게 아래만 살짝 누른다 */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-12 bg-[linear-gradient(0deg,rgb(0_0_0/0.45),transparent)]"
            />
          </div>

          {/* 상품 글 — 목록 카드(ProductMeta)와 같은 순서, 크게 */}
          <div className="mt-5 flex w-full min-w-0 flex-col items-center over10:mt-0 over10:items-start over10:pb-1">
            {c.preview && (
              <span className="mb-2.5 rounded-full bg-sub/15 px-2.5 py-1 text-c12 leading-none font-bold text-gem">
                출시 예정
              </span>
            )}
            <h1 className="text-c30 leading-[1.15] font-black tracking-[-0.02em] [overflow-wrap:anywhere] text-white over10:text-c44 over10:leading-[1.1]">
              {c.name}
              {c.age ? (
                <small className="ml-1.5 align-baseline text-c15 font-semibold tracking-normal text-t3 over10:text-c18">
                  {c.age}세
                </small>
              ) : null}
            </h1>
            {c.subtitle && (
              <p className="mt-1.5 text-c14 text-t2 over10:text-c15">
                {c.subtitle}
              </p>
            )}

            {(adult ||
              (!c.preview &&
                (typeof stats?.likes === 'number' ||
                  typeof stats?.chats === 'number'))) && (
              <p className="mt-3 flex items-center gap-3.5 text-c13 font-semibold text-t2 tabular-nums over10:text-c14">
                {!c.preview && (
                  <>
                    {typeof stats?.likes === 'number' && (
                      <span
                        className="inline-flex items-center gap-1"
                        aria-label={`좋아요 ${stats.likes}`}
                      >
                        <IoHeart aria-hidden="true" className="size-4" />
                        {kfmt(stats.likes)}
                      </span>
                    )}
                    {typeof stats?.chats === 'number' && (
                      <span
                        className="inline-flex items-center gap-1"
                        aria-label={`대화 ${stats.chats}`}
                      >
                        <ChatIcon aria-hidden="true" className="size-4" />
                        {kfmt(stats.chats)}
                      </span>
                    )}
                  </>
                )}
                {adult && (
                  <span className="rounded-control px-1.5 text-c11 leading-[1.6] font-bold text-coral shadow-[inset_0_0_0_1px_currentColor]">
                    19+
                  </span>
                )}
              </p>
            )}

            {c.tags.length > 0 && (
              <ul
                aria-label="태그"
                className="mt-3 flex max-w-[440px] list-none flex-wrap justify-center gap-1.5 p-0 over10:justify-start"
              >
                {/* 머리에선 6개까지 — 태그가 10개면 모바일 가운데 정렬로 네 줄이 된다 */}
                {c.tags.slice(0, 6).map((t) => (
                  <li
                    key={t}
                    className="inline-flex h-7 items-center rounded-full bg-white/10 px-3 text-c12 font-medium text-white/90 backdrop-blur-md"
                  >
                    #{t}
                  </li>
                ))}
              </ul>
            )}

            {author && (
              <p className="mt-3.5 flex min-w-0 items-center gap-1.5 text-c13 text-t3 over10:text-c14">
                <span className="min-w-0 truncate">{author}</span>
                {c.official && (
                  <span className="flex-none rounded-control bg-foreground px-1.25 text-c10 leading-[1.7] font-extrabold text-background">
                    공식
                  </span>
                )}
                {/* 제작자 등급 자리 — 기준이 정해지면 값만 내려오면 된다(ProductMeta 와 같은 모양) */}
                {c.creatorGrade && (
                  <span className="flex-none rounded-control px-1 text-c10 leading-[1.6] font-bold text-t2 shadow-[inset_0_0_0_1px_var(--color-line2)]">
                    {c.creatorGrade}
                  </span>
                )}
              </p>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
